import React, { useState, useEffect, useMemo } from 'react';
import { 
    Megaphone, Plus, Trash2, Edit2, Loader2, Image as ImageIcon, 
    Sparkles, Check, X, Calendar, Globe, Power, Clock, Save, CheckCircle2,
    Languages, Library
} from 'lucide-react';
import { fetchAllNotices, upsertNotice, deleteNotice } from '../../../services/adminService';
import { Notice, User } from '../../../types';
import { useLanguage } from '../../../context/LanguageContext';
import { ConfirmModal } from '../../Common/ConfirmModal';
import { at } from '../../../i18nAdmin';
import { GoogleGenAI } from "@google/genai";
import { translateArticle, translateNoticeMulti } from '../../../services/gemini';
import { formatLocalDateTime } from '../../../services/formatService';
import { supabase } from '../../../services/authService';

export const NoticesTab: React.FC = () => {
    const { lang } = useLanguage();
    const [notices, setNotices] = useState<Notice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [noticeToDelete, setNoticeToDelete] = useState<string | null>(null);
    const [isFormAnimating, setIsFormAnimating] = useState(false);
    const [editingNotice, setEditingNotice] = useState<Partial<Notice> | null>(null);
    
    // AI Image state
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const [aiPrompt, setAiPrompt] = useState(lang === 'ko' ? `모바일 앱 모달 배너 디자인.
배경은 깔끔한 단색 또는 은은한 그라디언트 배경(블루 또는 퍼플 톤),
전체적으로 플랫하고 미니멀한 스타일.
이미지의 오른쪽에만 하나의 메인 오브젝트를 배치하고,
왼쪽 영역은 텍스트를 넣을 수 있도록 충분히 비워둔다.` : `Mobile app modal banner design.
Clean solid color or subtle gradient background (blue or purple tones),
Overall flat and minimalist style.
Main object placed only on the right side of the image,
Left area left empty for text.`);

    // Extract unique existing banners for reuse
    const existingBanners = useMemo(() => {
        const banners = notices
            .map(n => n.banner)
            .filter((b): b is string => !!b);
        return Array.from(new Set(banners));
    }, [notices]);

    useEffect(() => {
        loadNotices();
    }, []);

    const loadNotices = async () => {
        setIsLoading(true);
        try {
            const data = await fetchAllNotices();
            // Sort by created_at descending just in case service doesn't
            setNotices([...data].sort((a, b) => 
                new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
            ));
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const openCreateForm = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        setEditingNotice({
            title_ko: '', title_en: '',
            content_ko: '', content_en: '',
            description_ko: '', description_en: '',
            banner: null,
            is_published: true,
            start_at: null,
            end_at: null,
            user_id: user?.id || ''
        });
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const openEditForm = (notice: Notice) => {
        setEditingNotice(notice);
        setIsFormOpen(true);
        setTimeout(() => setIsFormAnimating(true), 10);
    };

    const closeForm = () => {
        setIsFormAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            setEditingNotice(null);
            setAiPrompt('');
        }, 500);
    };

    const handleAutoTranslate = async (direction: 'toEN' | 'toKR') => {
        if (!editingNotice) return;
        
        const sourceTitle = direction === 'toEN' ? editingNotice.title_ko : editingNotice.title_en;
        const sourceContent = direction === 'toEN' ? editingNotice.content_ko : editingNotice.content_en;
        const sourceDesc = direction === 'toEN' ? editingNotice.description_ko : editingNotice.description_en;
        
        if (!sourceTitle && !sourceContent) {
            alert(at(lang, 'enterContentTranslate'));
            return;
        }

        setIsTranslating(true);
        try {
            if (direction === 'toEN') {
                // To EN and others
                const result = await translateNoticeMulti(sourceTitle || '', sourceContent || '', sourceDesc || '');
                setEditingNotice(prev => ({
                    ...prev,
                    title_en: result.en.title,
                    content_en: result.en.content,
                    description_en: result.en.description,
                    // title_ja: result.ja.title,
                    // content_ja: result.ja.content,
                    // description_ja: result.ja.description,
                    // title_es: result.es.title,
                    // content_es: result.es.content,
                    // description_es: result.es.description,
                    // title_fr: result.fr.title,
                    // content_fr: result.fr.content,
                    // description_fr: result.fr.description
                }));
            } else {
                // To KO
                const result = await translateArticle(sourceTitle || '', sourceContent || '', 'Korean');
                setEditingNotice(prev => ({
                    ...prev,
                    title_ko: result.title,
                    content_ko: result.content
                }));
            }
        } catch (e) {
            alert('AI Translation failed');
        } finally {
            setIsTranslating(false);
        }
    };

    const handleSave = async () => {
        if (!editingNotice) return;
        setIsSaving(true);
        try {
            let finalNotice = { ...editingNotice };
            
            // 자동으로 영문/일본어/스페인어/프랑스어 번역 수행 (한국어 기반)
            if (editingNotice.title_ko && editingNotice.content_ko) {
                try {
                    const translations = await translateNoticeMulti(
                        editingNotice.title_ko, 
                        editingNotice.content_ko, 
                        editingNotice.description_ko || ''
                    );
                    
                    finalNotice = {
                        ...finalNotice,
                        title_en: translations.en.title,
                        content_en: translations.en.content,
                        description_en: translations.en.description,
                        // title_ja: translations.ja.title,
                        // content_ja: translations.ja.content,
                        // description_ja: translations.ja.description,
                        // title_es: translations.es.title,
                        // content_es: translations.es.content,
                        // description_es: translations.es.description,
                        // title_fr: translations.fr.title,
                        // content_fr: translations.fr.content,
                        // description_fr: translations.fr.description
                    };
                } catch (translationError) {
                    console.error('Auto translation failed, saving original only', translationError);
                }
            }

            await upsertNotice(finalNotice);
            await loadNotices();
            closeForm();
        } catch (e) {
            alert('Failed to save notice');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = (id: string) => {
        setNoticeToDelete(id);
        setIsConfirmModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!noticeToDelete) return;
        setIsDeleting(true);
        try {
            await deleteNotice(noticeToDelete);
            setNotices(prev => prev.filter(n => n.id !== noticeToDelete));
            setIsConfirmModalOpen(false);
            setNoticeToDelete(null);
        } catch (e) {
            alert('Failed to delete notice');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleGenerateBanner = async () => {
        if (!aiPrompt.trim()) return;
        setIsGeneratingImage(true);
        try {
            const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_GOOGLE_API_KEY });
            const model = import.meta.env.VITE_GEMINI_IMG_MODEL;
            const prompt = `A professional, high-quality banner for a football app announcement. Theme: ${aiPrompt}. Style: 3D render, vibrant, modern, cinematic lighting. No text.`;
            
            const response = await ai.models.generateContent({
                model,
                contents: [{ parts: [{ text: prompt }] }],
                config: { imageConfig: { aspectRatio: "1:1" } }
            });

            for (const part of response.candidates[0].content.parts) {
                if (part.inlineData) {
                    const base64Data = part.inlineData.data;
                    const url = `data:image/png;base64,${base64Data}`;
                    setEditingNotice(prev => prev ? { ...prev, banner: url } : null);
                    break;
                }
            }
        } catch (e) {
            alert('AI Image generation failed');
        } finally {
            setIsGeneratingImage(false);
        }
    };

    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">{at(lang, 'notices')}</h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">{at(lang, 'manageNoticesDesc')}</p>
                </div>
                <button 
                    onClick={openCreateForm}
                    className="flex items-center gap-3 bg-slate-900 text-white px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-rose-500 transition-all active:scale-95"
                >
                    <Plus size={18} strokeWidth={3} />
                    {at(lang, 'createNotice')}
                </button>
            </div>

            <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
                {isLoading ? (
                    <div className="py-32 flex flex-col items-center justify-center gap-4">
                        <div className="relative w-10 h-10">
                            <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
                            <div className="absolute inset-0 border-4 border-t-rose-500 rounded-full animate-spin" />
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">{at(lang, 'syncingIntelHub')}</span>
                    </div>
                ) : notices.length === 0 ? (
                    <div className="py-40 text-center opacity-20">
                        <Megaphone size={64} className="mx-auto mb-6" />
                        <p className="text-sm font-black uppercase tracking-widest">{at(lang, 'noActiveCommunications')}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                                    <th className="px-8 py-5 min-w-[200px] max-w-[300px]">{at(lang, 'intelAsset')}</th>
                                    <th className="px-6 py-5 min-w-[120px]">{at(lang, 'status')}</th>
                                    <th className="px-6 py-5 min-w-[220px]">{at(lang, 'timeline')}</th>
                                    <th className="px-8 py-5 text-right min-w-[120px]">{at(lang, 'ops')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {notices.map(notice => (
                                    <tr key={notice.id} className="md:hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-5 max-w-[280px]">
                                                <div className="w-12 h-12 rounded-2xl bg-slate-100 overflow-hidden border border-white shadow-sm ring-1 ring-slate-100 shrink-0">
                                                    {notice.banner ? (
                                                        <img src={notice.banner} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-slate-300"><Megaphone size={20} /></div>
                                                    )}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="text-sm font-black text-slate-900 truncate block uppercase tracking-tight leading-tight">{notice.title_ko}</span>
                                                    <span className="text-[9px] font-bold text-slate-400 truncate block uppercase tracking-tighter mt-1">via {notice.title_en}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6">
                                            {notice.is_published ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                                    <CheckCircle2 size={12} strokeWidth={3} /> {at(lang, 'live')}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-400 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                                    <Power size={12} strokeWidth={3} /> {at(lang, 'offline')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1.5 text-slate-500 font-black text-[9px] uppercase tracking-tighter">
                                                    <Clock size={10} /> 
                                                    {notice.start_at ? formatLocalDateTime(notice.start_at, lang) : at(lang, 'immediate')} — {notice.end_at ? formatLocalDateTime(notice.end_at, lang) : at(lang, 'infinite')}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button onClick={() => openEditForm(notice)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-indigo-600 md:hover:border-indigo-600 rounded-xl shadow-sm transition-all active:scale-90"><Edit2 size={16}/></button>
                                                <button onClick={() => handleDeleteClick(notice.id)} className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-xl shadow-sm transition-all active:scale-90"><Trash2 size={16}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Notice Form Modal */}
            {isFormOpen && editingNotice && (
                <div className={`fixed inset-0 z-[350] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !mt-0 ${isFormAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={closeForm}></div>
                    <div className={`relative bg-white w-full max-w-3xl h-[92vh] md:h-[90vh] rounded-t-[40px] md:rounded-[40px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isFormAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
                        <header className="p-8 border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white shadow-lg shadow-slate-100">
                                    <Megaphone size={24} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight leading-none">{editingNotice.id ? at(lang, 'editCommunication') : at(lang, 'newBroadcast')}</h3>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{at(lang, 'sysIntelModule')}</p>
                                </div>
                            </div>
                            <button onClick={closeForm} className="p-2 text-slate-300 md:hover:text-slate-900 transition-colors"><X size={28} strokeWidth={2.5} /></button>
                        </header>

                        <div className="flex-1 overflow-y-auto p-8 md:p-10 custom-scrollbar bg-slate-50/30">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                                {/* Left: Content Inputs */}
                                <div className="lg:col-span-7 space-y-12">
                                    <section className="space-y-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <Globe size={14} className="text-rose-500" />
                                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'language')}: {at(lang, 'korean')}</h4>
                                            </div>
                                        </div>
                                        <input 
                                            type="text" placeholder={at(lang, 'titleKo')}
                                            className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 font-black text-slate-900 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 transition-all shadow-sm"
                                            value={editingNotice.title_ko || ''}
                                            onChange={e => setEditingNotice({ ...editingNotice, title_ko: e.target.value })}
                                        />
                                        <input 
                                            type="text"
                                            className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 transition-all shadow-sm"
                                            value={editingNotice.content_ko || ''}
                                            onChange={e => setEditingNotice({ ...editingNotice, content_ko: e.target.value })}
                                        />
                                        <textarea 
                                            placeholder={at(lang, 'contentKo')} rows={12}
                                            className="w-full bg-white border border-slate-200 rounded-[28px] px-6 py-5 font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 transition-all shadow-sm resize-none"
                                            value={editingNotice.description_ko || ''}
                                            onChange={e => setEditingNotice({ ...editingNotice, description_ko: e.target.value })}
                                        />
                                    </section>
                                </div>

                                {/* Right: Metadata & Assets */}
                                <div className="lg:col-span-5 space-y-8">
                                    <section>
                                        <div className="bg-white border border-slate-200 rounded-[32px] p-6 space-y-6 shadow-sm">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-black text-slate-900 uppercase tracking-widest">{at(lang, 'publishStatus')}</span>
                                                <button 
                                                    onClick={() => setEditingNotice({ ...editingNotice, is_published: !editingNotice.is_published })}
                                                    className={`w-12 h-6 rounded-full p-1 transition-all ${editingNotice.is_published ? 'bg-emerald-500' : 'bg-slate-200'}`}
                                                >
                                                    <div className={`w-4 h-4 bg-white rounded-full transition-transform ${editingNotice.is_published ? 'translate-x-6' : 'translate-x-0'}`} />
                                                </button>
                                            </div>

                                            <div className="space-y-4">
                                                <div className="space-y-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{at(lang, 'transmissionStart')}</label>
                                                    <input 
                                                        type="datetime-local"
                                                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-xs font-bold text-slate-600 outline-none"
                                                        value={editingNotice.start_at ? new Date(new Date(editingNotice.start_at).getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 16) : ''}
                                                        onChange={e => setEditingNotice({ ...editingNotice, start_at: e.target.value })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{at(lang, 'transmissionExpiry')}</label>
                                                    <input 
                                                        type="datetime-local"
                                                        className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5 text-xs font-bold text-slate-600 outline-none"
                                                        value={editingNotice.end_at ? new Date(new Date(editingNotice.end_at).getTime() + 9 * 60 * 60 * 1000).toISOString().slice(0, 16) : ''}
                                                        onChange={e => setEditingNotice({ ...editingNotice, end_at: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 my-6">
                                            <ImageIcon size={14} className="text-emerald-500" />
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'activeBanner')}</h4>
                                        </div>
                                        
                                        <div className="aspect-video w-full rounded-[32px] bg-slate-100 border border-slate-200 overflow-hidden mb-8 shadow-inner flex items-center justify-center group relative">
                                            {editingNotice.banner ? (
                                                <>
                                                    <img src={editingNotice.banner} className="w-full h-full object-cover animate-in fade-in duration-300" />
                                                    <button onClick={() => setEditingNotice({ ...editingNotice, banner: null })} className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full opacity-0 md:group-hover:opacity-100 transition-opacity shadow-lg"><X size={16}/></button>
                                                </>
                                            ) : (
                                                <div className="text-center p-10 opacity-30">
                                                    <ImageIcon size={48} className="mx-auto mb-4" />
                                                    <p className="text-[10px] font-black uppercase tracking-widest">{at(lang, 'noBannerSelected')}</p>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="bg-white border border-slate-200 rounded-[32px] p-6 shadow-sm">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Sparkles size={14} className="text-amber-500" />
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'aiContentForge')}</span>
                                            </div>
                                            <textarea 
                                                placeholder={at(lang, 'enterVisualTheme')}
                                                className="w-full bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3 text-xs font-bold text-slate-700 outline-none mb-4 resize-none"
                                                rows={4}
                                                value={aiPrompt}
                                                onChange={e => setAiPrompt(e.target.value)}
                                            />
                                            <button 
                                                onClick={handleGenerateBanner}
                                                disabled={isGeneratingImage || !aiPrompt.trim()}
                                                className="w-full bg-slate-900 text-white py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 active:scale-95 disabled:opacity-30 transition-all shadow-lg shadow-slate-100"
                                            >
                                                {isGeneratingImage ? <Loader2 className="animate-spin" size={16} /> : <><Sparkles size={16} /> {at(lang, 'forgeNewVisual')}</>}
                                            </button>
                                        </div>
                                    </section>
                                </div>
                            </div>

                            {/* Reuse Image Library - Moved to bottom for full width layout */}
                            {existingBanners.length > 0 && (
                                <div className="pt-10">
                                    <div className="flex items-center gap-2 mb-6">
                                        <Library size={18} className="text-indigo-500" />
                                        <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{at(lang, 'assetLibrary')}</h4>
                                    </div>
                                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 p-1">
                                        {existingBanners.map((url, i) => (
                                            <button 
                                                key={i}
                                                onClick={() => setEditingNotice({ ...editingNotice, banner: url })}
                                                className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all md:hover:scale-105 ${editingNotice.banner === url ? 'border-rose-500 ring-4 ring-rose-100 shadow-xl' : 'border-slate-100'}`}
                                            >
                                                <img src={url} className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <footer className="p-8 border-t border-slate-100 bg-white flex gap-4 shrink-0">
                            <button 
                                onClick={handleSave}
                                disabled={isSaving}
                                className="flex-1 bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] shadow-2xl shadow-slate-100 active:scale-95 disabled:opacity-50 transition-all flex items-center justify-center gap-3"
                            >
                                {isSaving ? <Loader2 className="animate-spin" size={18} /> : <><Save size={18} /> {editingNotice.id ? at(lang, 'deployIntelligence') : (lang === 'ko' ? '번역 후 등록' : 'Translate & Deploy')}</>}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={isConfirmModalOpen}
                onClose={() => !isDeleting && setIsConfirmModalOpen(false)}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title={at(lang, 'deleteNotice')}
                description={at(lang, 'deleteNoticeConfirmDesc')}
                confirmLabel={at(lang, 'delete')}
                cancelLabel={at(lang, 'cancel')}
            />
        </div>
    );
};
